import React, { useState, useEffect } from 'react';
import { Settings, Activity, Clock, Users } from 'lucide-react';
import { useSelector } from 'react-redux';
import InitiateJob from './Tabs/InitiateJob';
import RunningJobs from './Tabs/RunningJobs';
import ScheduledJobs from './Tabs/ScheduledJobs';
import PermissionDeniedModal from '../../components/Modals/PermissionDeniedModal';
import AuthorizedUsers from './Tabs/AuthorizedUsers';

const BatchJob = () => {
  const [activeTab, setActiveTab] = useState('initiate');
  const [permissionDeniedModal, setPermissionDeniedModal] = useState({
    visible: false,
    message: ''
  });
  
  const user = useSelector(state => state?.auth?.user);
  const batchJobPermissions = user?.batchJobPermissions || [];
  const isAdmin = user?.role === 'admin';

  const hasTabPermission = (tabId) => {
    switch (tabId) {
      case 'initiate':
        return isAdmin || batchJobPermissions.includes('view_batch_jobs_initialisation');
      case 'running':
        return isAdmin || batchJobPermissions.includes('view_running_batch_jobs');
      case 'scheduled':
        return isAdmin || batchJobPermissions.includes('view_scheduled_batch_jobs');
      case 'permissions':
        return isAdmin;
      default:
        return false;
    }
  };


  // Handle tab change with permission check
  const handleTabChange = (tabId) => {
    if (hasTabPermission(tabId)) {
      setActiveTab(tabId);
    } else {
      let message = '';
      switch (tabId) {
        case 'initiate':
          message = "You don't have permission to view batch jobs initialization. Please contact your administrator if you believe this is a mistake.";
          break;
        case 'running':
          message = "You don't have permission to view running jobs. Please contact your administrator if you believe this is a mistake.";
          break;
        case 'scheduled':
          message = "You don't have permission to view scheduled jobs. Please contact your administrator if you believe this is a mistake.";
          break;
        default:
          message = "You don't have permission to access this section.";
      }
      setPermissionDeniedModal({ visible: true, message });
    }
  };

  const tabs = [
    { 
      id: 'initiate', 
      name: 'Job Initialisation', 
      icon: Settings,
      permission: hasTabPermission('initiate')
    },
    { 
      id: 'running', 
      name: 'Running Jobs', 
      icon: Activity,
      permission: hasTabPermission('running')
    },
    { 
      id: 'scheduled', 
      name: 'Scheduled Jobs', 
      icon: Clock,
      permission: hasTabPermission('scheduled')
    }
  ];
  
  // Admin-only tab
  if (isAdmin) {
    tabs.push({
      id: 'permissions',
      name: 'Authorized Users',
      icon: Users,
      permission: true
    });
  }

  // Filter tabs to only show those the user has permission for
  const visibleTabs = tabs.filter(tab => tab.permission);

  const renderTabContent = () => {
    if (!hasTabPermission(activeTab)) {
      return null;
    }

    switch (activeTab) {
      case 'initiate':
        return <InitiateJob 
          canTriggerJob={isAdmin || batchJobPermissions.includes('trigger_batch_job')} 
          canScheduleJob={isAdmin || batchJobPermissions.includes('schedule_batch_job')}
        />;
      case 'running':
        return <RunningJobs />;
      case 'scheduled':
        return <ScheduledJobs 
          canEdit={isAdmin || batchJobPermissions.includes('edit_scheduled_batch_job')}
          canDelete={isAdmin || batchJobPermissions.includes('delete_scheduled_batch_job')}
        />;
      case 'permissions':
        return isAdmin && <AuthorizedUsers />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen p-8 bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 font-display mb-2">
            Batch Jobs
          </h1>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                      ${activeTab === tab.id
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {renderTabContent()}
        </div>
      </div>

      {/* Permission Denied Modal */}
      <PermissionDeniedModal
        visible={permissionDeniedModal.visible}
        onClose={() => setPermissionDeniedModal({ visible: false, message: '' })}
        message={permissionDeniedModal.message}
      />
    </div>
  );
};

export default BatchJob;